import { Object3D } from 'three';
import DomainModel from './DomainModel';
import SelectionManager from './SelectionManager';
import CommandHistory from './CommandHistory';
import RenderPipeline from './RenderPipeline';
import { Picker } from './view3d';
import logging from './logging';
import EventDispatcher from './EventDispatcher';
import DefaultsManager from './DefaultsManager';

const log = logging('Application');

export default abstract class Application {
    model: DomainModel;
    object: Object3D;

    selection: SelectionManager = new SelectionManager(this);
    history: CommandHistory = new CommandHistory();
    pipeline: RenderPipeline;
    picker: Picker;
    defaults: DefaultsManager;

    private commands = new EventDispatcher();

    constructor() {
        window.addEventListener('app-command', this.handleCommand.bind(this));

        this.command('undo', () => {
            this.history.undo();
            this.refresh();
        });

        this.command('redo', () => {
            this.history.redo();
            this.refresh();
        });

        this.command('pipeline-update', () => {
            this.pipeline.update = true;
        });

        this.command('refresh', () => {
            this.refresh();
        });

    }

    /**
     * The implementation should begin running.
     */
    abstract start(): Promise<void>;

    /**
     * The implementation should discard its old layout, construct a new one
     * from its domain model, and replace the object with a fresh view of the
     * new model.
     */
    abstract refresh(): Promise<void>;

    /**
     * Register a handler for an application command. This could be done by an
     * app implementation itself, as with the undo/redo and pipeline-update
     * commands implemented in the base Application class, or they could be
     * implemented by any component of the application.
     *
     * Since commands as a concept are global and have a static lifetime, there
     * is no way to remove a command once it's registered. If you need
     * something like that, probably best to use an event bus of some sort,
     * e.g. the window's dispatchEvent() method.
     */
    command(action: string, handler: (...args: unknown[]) => unknown): void {
        this.commands.on(action, handler);
    }

    /**
     * Call `Application.do('command-name', ...)` to send a command to the
     * application instance. These commands are handled by functions registered
     * with the Application instance's command() method.
     */
    static do(action: string, ...args: any[]): void {
        window.dispatchEvent(new CustomEvent(
            'app-command', { detail: { action, args }}));
    }

    /**
     * This is the primary interface to the command history based on
     * serializing the root domain model. It has a side-effect: it recursively
     * commits changes to app.model. In fact, if changes are commited outside
     * of this method, then this method won't work correctly.
     * @param name If it's undefined, then repreated commits will not merge.
     * Otherwise, repeated commits under the same name will merge so that they
     * appear as a single undo/redo frame.
     * @param description A user visible string describing the action taken to
     * generate this unde frame, e.g. 'Add drawer'
     */
    commit(name: string | undefined, description: string): void {
        const oldData = this.model.toJSON();
        this.model.commit(true);
        const newData = this.model.toJSON();
        this.history.add({
            name,
            description,
            undo: () => {
                this.model =
                    (this.model.constructor as typeof DomainModel)
                    .fromJSON(oldData);
            },
            redo: () => {
                this.model =
                    (this.model.constructor as typeof DomainModel)
                    .fromJSON(newData);
            }
        });
        this.pipeline.update = true;
    }

    setLogLevel(module: string, level: string) {
        const mod = logging(module);
        switch(level.toUpperCase()) {
            case 'TRACE':
                mod.level = log.TRACE;
                break;
            case 'DEBUG':
                mod.level = log.DEBUG;
                break;
            case 'INFO':
                mod.level = log.INFO;
                break;
            case 'WARN':
                mod.level = log.WARN;
                break;
            case 'ERROR':
                mod.level = log.ERROR;
                break;
            default:
                log.error(`Unconvertible log level '${level}', expected TRACE, DEBUG, INFO, WARN, or ERROR (case insensitive).`);
        }
    }

    /**
     * This is a utility method to dispatch CustomEvents of type 'app-command'
     * to the Application implementation's command method.
     */
    private handleCommand(event: CustomEvent) {
        const { action, args } = event.detail;
        this.commands.trigger(action, ...args);
    }
}
